import {
    ActionButton,
    IconButton,
    Image,
    PersonaInitialsColor,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Text,
    TextField
} from '@fluentui/react';
import { useConfirmationModal } from '../../../../components/confirmationModal/ConfirmationModal';
import { Grid, GridCellProps, GridColumn as Column } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import Avatar from 'app/components/avatar/Avatar';
import { formatCurrency, formatRequestType, highlightTags, injectTargetToAnchor, ReplaceUrlByAnchor } from 'app/components/helpers';
import { unifiedDashboardCardsList } from 'app/models/common/enumerators';
import { IAssociationData, IRequestParams } from 'app/models/common/request';
import { RequestDto, RequestStatus } from 'generated/request-backend';
import { useRequestList } from 'app/services/request-api';
import associationImage from 'app/static/icons/association-icon.svg';
import unlinkIcon from 'app/static/icons/link-dismiss.svg';
import useStore, { IStore } from 'app/store';
import { getPortalValue } from 'app/utils/utilities';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import './AssociationTab.scss';
import { StringConstants } from 'app/utils/constants';
import DOMPurify from 'dompurify';
import { isNil } from 'lodash';
import { IColumnLookUpProps } from 'app/models/common/post';

interface IProps {
    setIsLinkRequestModalOpen: (isOpen: boolean) => void;
    selectedRequest: RequestDto;
    setSelectedRequest: (request: RequestDto) => void;
    setIsAssociatingParent: (isAssociating: boolean) => void;
    setIsEditMode: (isEditMode: boolean) => void;
    parent: RequestDto;
}

const AssociationTab: React.FC<IProps> = (props) => {
    const pageSize = 1000;
    const containerWidth = 1367;

    const history = useHistory();
    const portal = getPortalValue(history?.location?.pathname);

    const { setIsLinkRequestModalOpen, selectedRequest, setIsAssociatingParent, setSelectedRequest, setIsEditMode, parent } =
        props;

    const { t: translate } = useTranslation();
    const requestType = useStore((state: IStore) => state.requestType);

    const AssignedToCell = (props: { dataItem: RequestDto }) => {
        return (
            <td>
                {props.dataItem?.assignee ? (
                    <div className="assigned-to-cell">
                        <Avatar
                            userId={props.dataItem?.assignee?.id}
                            userName={props.dataItem?.assignee?.displayName}
                            initialColor={PersonaInitialsColor.coolGray}
                            imageSize={25}
                            roundedPic={true}
                        />
                        <span className="assigned-to-display-name">{props.dataItem?.assignee?.displayName}</span>
                    </div>
                ) : (
                    <div className="assigned-to-cell">
                        {' '}
                        <Avatar
                            userId={'0'}
                            userName={'Unassigned'}
                            initialColor={PersonaInitialsColor.coolGray}
                            imageSize={25}
                            roundedPic={true}
                        />
                        <span className="assigned-to-display-name">Unassigned</span>
                    </div>
                )}
            </td>
        );
    };

    const customerNameHelper = (dataItem: RequestDto) => {
        if (dataItem !== undefined && dataItem.customerImpacts !== undefined && dataItem?.customerImpacts[0]?.customerName) {
            return dataItem?.customerImpacts[0]?.customerName;
        } else {
            return ' -';
        }
    };

    const TitleCell = (props: { dataItem: RequestDto}) => {
        if (props.dataItem.isParent) {
            return <td title={props?.dataItem?.summary}>{props?.dataItem?.summary}</td>;
        } else {
            return <td title={customerNameHelper(props.dataItem)}>{customerNameHelper(props.dataItem)}</td>;
        }
    };

    const UnlinkCell = (props: { dataItem: RequestDto }) => {
        const { ConfirmationModal, showModal } = useConfirmationModal();
        if (selectedRequest.isParent && selectedRequest.unifiedTrackingId === props.dataItem.unifiedTrackingId) {
            return <></>;
        }

        const requestData = props.dataItem;
        const child = requestData.isParent ? selectedRequest : requestData;

        const childUnassociationItem: IAssociationData = {
            requestId: child.unifiedTrackingId,
            associatedRequestId: child.parentRequestId,
            expectedCustomerFamilySubmissionId: child.customerFamilySubmissionId,
            expectedCustomerFamilyGeneration: child.customerFamilyGeneration
        };

        return (
            <td>
                <ConfirmationModal
                    childUnassociationItem={childUnassociationItem}
                    message={'Do you want to unlink this child ticket?'}
                />
                <IconButton role="button" aria-label="unlink request" onClick={showModal}>
                    <Image src={unlinkIcon} alt="unlink request" />
                </IconButton>
            </td>
        );
    };

    const IdCell = (props: GridCellProps) => {
        return (
            <td>
                <ActionButton
                    styles={{ label: { fontWeight: 600, margin: 0 } }}
                    aria-label={`navigate to request ${props.dataItem[props.field]}`}
                    text={props.dataItem[props.field]}
                    onClick={onIdClick(props.dataItem)}
                />
            </td>
        );
    };

    const RequestTypeCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return <td title={formatRequestType(dataItem?.requestType)}> {formatRequestType(dataItem?.requestType)}</td>
    }

    const StatusCell = (props: GridCellProps) => {
        return <td title={RequestStatus[props?.dataItem?.status]}> {RequestStatus[props?.dataItem?.status]}</td>
    }

    const TotalRevenueCell = (props: GridCellProps) => {
        const dataItem = props?.dataItem as RequestDto;
        return <td title={formatCurrency(dataItem?.msxCurrentTotalRevenue)}> {formatCurrency(dataItem?.msxCurrentTotalRevenue)}</td>;
    }

    const setWidth = () => {
        let columnCount = columnsLookUpData.filter((item) => item.isChecked).length;
        if (
            requestType === StringConstants.MY_REQUEST &&
            columnsLookUpData.filter((item) => item.key === 0 && item.isChecked).length > 0
        ) {
            columnCount = columnCount - 1;
        }
        if (columnCount > 1) {
            let padding = 50 + 47 + 30;
            const availWidth = containerWidth - padding - 90;

            return parseInt((availWidth / columnCount).toString());
        } else {
            return containerWidth;
        }
    };

    const [columnsLookUpData] = useState([
        {
            key: 0,
            column: translate('ID'),
            isChecked: true
        },
        {
            key: 1,
            column: translate('Title'),
            isChecked: true
        },
        {
            key: 2,
            column: translate('Request Type'),
            isChecked: true
        },
        {
            key: 3,
            column: translate('Status'),
            isChecked: true
        },
        {
            key: 4,
            column: translate('Assigned To'),
            isChecked: true
        },
        {
            key: 5,
            column: translate('Total Revenue Risk'),
            isChecked: true
        },
        {
            key: 6,
            column: translate('Unlink'),
            isChecked: true
        }
    ]);

    const columnElements = [
        {
            key: 0,
            column: (
                <Column cell={IdCell} width={setWidth() - 50} className="cell-in-bold" field="unifiedTrackingId" title="ID" resizable={false} />
            )
        },
        {
            key: 1,
            column: <Column width={setWidth() - 30} field="Title" title="TITLE" cell={TitleCell} resizable={false} />
        },
        {
            key: 2,
            column: <Column width={setWidth()} title="REQUEST TYPE" field="requestType" cell={RequestTypeCell} resizable={false} />
        },
        {
            key: 3,
            column: <Column width={setWidth() - 50} field="status" title="STATUS" cell={StatusCell} resizable={false} />
        },
        {
            key: 4,
            column: (
                <Column width={setWidth() + 30} field="assignedTo" title="ASSIGNED TO" cell={AssignedToCell} resizable={false} />
            )
        },
        {
            key: 5,
            column: <Column width={setWidth() + 50} field="msxTotalRevenue" title="TOTAL REVENUE RISK" cell={TotalRevenueCell} resizable={false} />
        },
        {
            key: 6,
            column: (
                <Column
                    width={setWidth() - 30}
                    field="unlink"
                    title="UNLINK"
                    resizable={false}
                    className="right-aligned-column"
                    cell={UnlinkCell}
                />
            )
        }
    ];

    const childColumnElements = [
        {
            key: 0,
            column: (
                <Column cell={IdCell} width={setWidth() - 50} className="cell-in-bold" field="unifiedTrackingId" title="ID" resizable={false} />
            )
        },
        {
            key: 1,
            column: (
                <Column width={setWidth() + 30} field="Customer Name" title="CUSTOMER NAME" cell={TitleCell} resizable={false} />
            )
        },
        {
            key: 2,
            column: <Column cell={RequestTypeCell} width={setWidth()} title="REQUEST TYPE" field="requestType" resizable={false} />
        },
        {
            key: 3,
            column: <Column cell={StatusCell} width={setWidth() - 50} field="status" title="STATUS" resizable={false} />
        },
        {
            key: 4,
            column: (
                <Column width={setWidth() + 30} field="assignedTo" title="ASSIGNED TO" cell={AssignedToCell} resizable={false} />
            )
        },
        {
            key: 5,
            column: <Column cell={TotalRevenueCell} width={setWidth() + 50} field="msxTotalRevenue" title="TOTAL REVENUE RISK" resizable={false} />
        },
        {
            key: 6,
            column: (
                <Column
                    width={setWidth() - 30}
                    field="unlink"
                    title="UNLINK"
                    resizable={false}
                    className="right-aligned-column"
                    cell={UnlinkCell}
                />
            )
        }
    ];

    const [childColumnsLookUpData] = useState([
        {
            key: 0,
            column: translate('ID'),
            isChecked: true
        },
        {
            key: 1,
            column: translate('Customer Name'),
            isChecked: true
        },
        {
            key: 2,
            column: translate('Request Type'),
            isChecked: true
        },
        {
            key: 3,
            column: translate('Status'),
            isChecked: true
        },
        {
            key: 4,
            column: translate('Assigned To'),
            isChecked: true
        },
        {
            key: 5,
            column: translate('Total Revenue Risk'),
            isChecked: true
        },
        {
            key: 6,
            column: translate('Unlink'),
            isChecked: true
        }
    ]);

    const getColumns = (isParent: Boolean) => {
        let list: IColumnLookUpProps[] = [];
        if (isParent) {
            columnsLookUpData.forEach((item) => {
                columnElements.every((ele) => {
                    if (ele.key === item.key && item.isChecked) {
                        list.push(ele);
                        return false;
                    } else {
                        return true;
                    }
                });
            });
        } else {
            childColumnsLookUpData.forEach((item) => {
                childColumnElements.every((ele) => {
                    if (ele.key === item.key && item.isChecked) {
                        list.push(ele);
                        return false;
                    } else {
                        return true;
                    }
                });
            });
        }

        if (requestType === StringConstants.MY_REQUEST) {
            return list.filter((item: IColumnLookUpProps) => item.key !== 0);
        } else {
            return list;
        }
    };

    const [columnList, setColumnList] = useState(getColumns(false));
    const [childColumnList, setChildColumnList] = useState(getColumns(true));
    const [childRequests, setChildRequests] = useState<RequestDto[]>([]);
    const [criteria, setCriteria] = useState<IRequestParams>({
        from: '2001-01-01T00:00:00Z',
        to: '9999-12-31T23:59:59Z',
        pageNumber: 1,
        pageSize: pageSize,
        type: unifiedDashboardCardsList.ALL_REQUEST,
        requestType: [],
        parentRequestId: selectedRequest.parentRequestId,
        portal: portal
    });

    const [searchValue, setSearchValue] = useState('');
    const [filteredChildren, setFilteredChildren] = useState<RequestDto[]>([]);

    const { isLoading: isLoadingRequests } = useRequestList(criteria, {
        enabled: !isNil(criteria) && !isNil(criteria.parentRequestId),
        onSuccess: (response) => {
            setChildRequests(response ? response.items : []);
            setFilteredChildren(response ? response.items : []);
        }
    });

    useEffect(() => {
        setSearchValue('');
    }, []);

    useEffect(() => {
        setColumnList(getColumns(true));
        setChildColumnList(getColumns(false));
    }, [selectedRequest]);

    useEffect(() => {
        setCriteria({ ...criteria, parentRequestId: parent?.unifiedTrackingId });
    }, [parent]);

    const handleOpenAssociateRequestModal = (associatingParent: boolean) => {
        setIsAssociatingParent(associatingParent);
        setIsLinkRequestModalOpen(true);
    };

    const textInput = useRef(null);

    const handleSearch = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setSearchValue((e.target as HTMLInputElement | HTMLTextAreaElement).value);
        setFilteredChildren(
            childRequests.filter(
                (child: RequestDto) =>
                    child.unifiedTrackingId.toLowerCase().includes((e.target as HTMLInputElement | HTMLTextAreaElement).value.toLowerCase()) ||
                    child.summary.toLowerCase().includes((e.target as HTMLInputElement | HTMLTextAreaElement).value.toLowerCase())
            )
        );
    };

    const onIdClick = (item: RequestDto) => () => {
        setIsEditMode(false);
        setSelectedRequest(item);
    };

    return (
        <>
            {(selectedRequest.parentRequestId !== '' && selectedRequest.parentRequestId !== null) ||
                selectedRequest.isParent === true ? (
                <div className={'association-table-cont table-cont '}>
                    <div className="pr-table-name">
                        <Text className="text-bold">Associated parent:</Text>
                    </div>
                    <div className={'grid-cont'}>
                        <Tooltip openDelay={100} position="right" anchorElement="target">
                            <Grid data={[parent]} dataItemKey={'id'} sortable={true} resizable={false}>
                                {columnList?.map((item: IColumnLookUpProps) => item.column)}
                            </Grid>
                        </Tooltip>
                    </div>
                    <div>
                        {selectedRequest.isParent !== true
                            && <Text><span
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(highlightTags(
                                        ReplaceUrlByAnchor(
                                            injectTargetToAnchor(parent?.description),
                                            'link-item',
                                            true
                                        )
                                    ))
                                }}
                            ></span>
                            </Text>
                        }
                    </div>
                    <div className="cr-panel-wrapper">
                        <div className={''}>
                            <div className="dp-header">
                                <div className="head-col-left">
                                    <Text className="dp-head-title">{`${childRequests.length} child requests`}</Text>
                                    <span
                                        className="associate-more-container"
                                        onClick={() => handleOpenAssociateRequestModal(false)}>
                                        <Text className="txt-link">Associate More</Text>
                                    </span>
                                </div>
                                <div className="head-col-right">
                                    <TextField
                                        value={searchValue}
                                        onChange={handleSearch}
                                        placeholder="Enter Unified Tracking ID or Title"
                                        autoComplete={'off'}
                                        componentRef={textInput}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className={'grid-cont'}>
                        <Tooltip openDelay={100} position="right" anchorElement="target">
                            {isLoadingRequests ? (
                                <div>
                                    <div className="m-t-50">
                                        <Spinner label="Loading requests..." size={SpinnerSize.large} />
                                    </div>
                                </div>
                            ) : (
                                <Grid data={filteredChildren} dataItemKey={'id'} sortable={true} resizable={false}>
                                    {childColumnList?.map((item: IColumnLookUpProps) => item.column)}
                                </Grid>
                            )}
                        </Tooltip>
                    </div>
                </div>
            ) : (
                <div className="no-association-content">
                    <div>
                        <Image src={associationImage} alt="associateRequest" />
                    </div>
                    <div className="no-association-text">
                        <Text> You haven't established any association yet for this request</Text>
                    </div>
                    <div className="primary-associate-button">
                        <PrimaryButton className="primary add-cust-btn" onClick={() => handleOpenAssociateRequestModal(true)}>
                            Associate Now
                        </PrimaryButton>
                    </div>
                </div>
            )}
        </>
    );
};

export default AssociationTab;
